import Icon from '@mui/material/Icon';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { EmailData } from '@/components/email-viewer/email-viewer-types';
import EdiTransactionViewer from './EdiTransactionViewer';
import { ModernPDFViewer } from '@/components/accounts-payable/invoice-detail/modern-pdf-viewer';
import type { SourceFlags } from './utils';

interface DocumentData {
  url: string;
  originalFileName: string;
  mimeType?: string;
}

export interface SourceDocumentViewerProps {
  sourceFlags: SourceFlags;
  emailData?: EmailData;
  conversationId?: string;
  ordefulMessage?: Record<string, unknown>;
  document?: DocumentData;
}

export function SourceDocumentViewer({
  sourceFlags,
  emailData,
  conversationId,
  ordefulMessage,
  document,
}: SourceDocumentViewerProps) {
  const { isEdiSource, isEmailSource, isDirectEntry, isFileUpload } =
    sourceFlags;

  // Email source
  if (isEmailSource && emailData?.inboundEmailId) {
    return (
      <SmartEmailViewer
        emailData={emailData}
        conversationId={conversationId}
        fetchOCR
        fetchExtractedPurchaseOrder
      />
    );
  }

  // EDI source
  if (isEdiSource && ordefulMessage) {
    return <EdiTransactionViewer ordefulMessage={ordefulMessage} />;
  }

  // File upload source
  if (isFileUpload && document) {
    return (
      <MDBox
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <MDBox
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            bgcolor: 'grey.50',
          }}
        >
          <MDTypography variant="caption" color="text.secondary">
            {document.originalFileName}
          </MDTypography>
        </MDBox>
        <MDBox
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
          }}
        >
          {document.mimeType === 'application/pdf' ? (
            <ModernPDFViewer url={document.url} width={600} />
          ) : document.mimeType?.startsWith('image/') ? (
            <img
              src={document.url}
              alt={document.originalFileName}
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          ) : (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <MDTypography color="text.secondary">
                Preview not available for this file type
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    );
  }

  // Direct entry source
  if (isDirectEntry) {
    return (
      <MDBox
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          p: 4,
          bgcolor: 'grey.50',
        }}
      >
        <Icon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }}>edit_note</Icon>
        <MDTypography variant="h6" color="text.secondary" mb={1}>
          Direct Entry
        </MDTypography>
        <MDTypography variant="body2" color="text.secondary" textAlign="center">
          This sales order was created through direct data entry
        </MDTypography>
      </MDBox>
    );
  }

  // No content to display
  return null;
}
